import { type FormEvent, type ReactNode, useMemo, useState } from 'react';

type SubmitStatus = 'idle' | 'confirm' | 'submitting' | 'success' | 'error';

type FormDataState = {
  name: string;
  company: string;
  email: string;
  phone: string;
  service: string;
  message: string;
  privacyAccepted: boolean;
};

const initialFormData: FormDataState = {
  name: '',
  company: '',
  email: '',
  phone: '',
  service: '',
  message: '',
  privacyAccepted: false
};

function Modal({
  isOpen,
  title,
  children,
  actions,
  closeOnBackdrop = true,
  onClose
}: {
  isOpen: boolean;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
  closeOnBackdrop?: boolean;
  onClose?: () => void;
}) {
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-4"
      onClick={() => {
        if (closeOnBackdrop && onClose) {
          onClose();
        }
      }}
    >
      <div
        className="w-full max-w-xl rounded-3xl border border-slate-200 bg-white p-8 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="mb-4 text-2xl font-bold text-slate-900">{title}</h3>
        <div className="text-slate-700">{children}</div>
        {actions && <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">{actions}</div>}
      </div>
    </div>
  );
}

export default function Home() {
  const [formData, setFormData] = useState<FormDataState>(initialFormData);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [dialogMessage, setDialogMessage] = useState('');

  const confirmationItems = useMemo(
    () => [
      { label: 'お名前', value: formData.name },
      { label: '会社名', value: formData.company },
      { label: 'メールアドレス', value: formData.email },
      { label: '電話番号', value: formData.phone || '未入力' },
      { label: 'ご希望のサービス', value: formData.service },
      { label: 'お問い合わせ内容', value: formData.message }
    ],
    [formData]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = event.target;

    if (type === 'checkbox') {
      const checked = (event.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!formData.privacyAccepted) {
      setDialogMessage('プライバシーポリシーに同意してください。');
      setSubmitStatus('error');
      return false;
    }

    if (formData.message.length > 500) {
      setDialogMessage('お問い合わせ内容は500文字以内で入力してください。');
      setSubmitStatus('error');
      return false;
    }

    return true;
  };

  const handleOpenConfirm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!event.currentTarget.reportValidity()) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setSubmitStatus('confirm');
  };

  const handleConfirmSubmit = async () => {
    setSubmitStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('company', formData.company);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);
      formBody.append('privacy', formData.privacyAccepted ? '同意する' : '');

      const response = await fetch('/send.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: formBody.toString()
      });

      const resultText = await response.text();

      if (response.ok && resultText.trim() === 'OK') {
        setFormData(initialFormData);
        setDialogMessage('お問い合わせを受け付けました。担当者より折り返しご連絡いたします。');
        setSubmitStatus('success');
        return;
      }

      setDialogMessage('送信に失敗しました。時間をおいて再度お試しください。');
      setSubmitStatus('error');
    } catch (error) {
      console.error('送信エラー:', error);
      setDialogMessage('通信エラーが発生しました。時間をおいて再度お試しください。');
      setSubmitStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 2px,
                rgba(255,255,255,0.1) 2px,
                rgba(255,255,255,0.1) 4px
              ), repeating-linear-gradient(
                -45deg,
                transparent,
                transparent 2px,
                rgba(255,255,255,0.1) 2px,
                rgba(255,255,255,0.1) 4px
              )`,
              backgroundSize: '60px 60px'
            }}
          ></div>
        </div>

        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-blue-600/20 to-transparent transform skew-x-12 translate-x-1/4"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center">
          <div className="inline-block mb-6 px-6 py-2 bg-blue-500/20 backdrop-blur-sm rounded-full border border-blue-400/30">
            <span className="text-blue-300 text-sm font-medium tracking-wider">SALES EXCELLENCE</span>
          </div>

          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-8 leading-tight">
            営業の<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">プロフェッショナル</span>が<br />
            あなたのビジネスを加速する
          </h1>

          <p className="text-xl md:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto leading-relaxed">
            合同会社FullBetは、営業代行から人材育成まで、<br />
            企業の営業活動を総合的にサポートする営業支援のスペシャリストです
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <a href="#contact" className="group px-10 py-5 bg-gradient-to-r from-blue-500 to-cyan-400 text-white text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer">
              無料相談を予約
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </a>
            <a href="#services" className="px-10 py-5 bg-white/10 backdrop-blur-sm text-white text-lg font-bold rounded-full border-2 border-white/30 hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer">
              サービス詳細
            </a>
          </div>

          <div className="grid grid-cols-3 gap-8 mt-20 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 mb-2">営業代行</div>
              <div className="text-blue-200 text-sm">新規開拓から伴走</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 mb-2">24/7</div>
              <div className="text-blue-200 text-sm">オンライン相談受付</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 mb-2">人材育成</div>
              <div className="text-blue-200 text-sm">継続的な支援体制</div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-4xl text-white/50"></i>
        </div>
      </section>

      <section id="services" className="py-32 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-blue-600 font-bold text-sm tracking-wider uppercase mb-4 block">Our Services</span>
            <h2 className="text-5xl font-bold text-slate-900 mb-6">包括的な営業支援サービス</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              営業のあらゆる課題に対応する、5つの専門サービスをご提供します
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-user-star-line',
                title: '営業代行業務',
                description: '経験豊富な営業のプロフェッショナルが、貴社の営業活動を代行。新規開拓から既存顧客のフォローまで、売上向上を実現します。',
                color: 'from-blue-500 to-cyan-400'
              },
              {
                icon: 'ri-lightbulb-line',
                title: '営業コンサルティング',
                description: '営業戦略の立案から実行まで、データに基づいた専門的なアドバイスを提供。貴社の営業力を根本から強化します。',
                color: 'from-purple-500 to-pink-400'
              },
              {
                icon: 'ri-megaphone-line',
                title: '営業プロモーション・マーケティング支援',
                description: '効果的なプロモーション戦略とマーケティング施策の企画・実行で、見込み客の獲得から商談化までをサポートします。',
                color: 'from-orange-500 to-red-400'
              },
              {
                icon: 'ri-graduation-cap-line',
                title: '営業研修・人材育成',
                description: '実践的な営業スキルを習得できる研修プログラムを提供。営業チーム全体のレベルアップを実現します。',
                color: 'from-green-500 to-emerald-400'
              },
              {
                icon: 'ri-team-line',
                title: '営業人材派遣',
                description: '即戦力となる営業人材を派遣し、貴社の営業力を即座に強化。繁忙期や新規事業立ち上げ時に最適です。',
                color: 'from-indigo-500 to-blue-400'
              }
            ].map((service, index) => (
              <div key={index} className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-slate-100">
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">{service.title}</h3>
                <p className="text-slate-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(
                0deg,
                transparent,
                transparent 3px,
                rgba(255,255,255,0.1) 3px,
                rgba(255,255,255,0.1) 6px
              )`,
              backgroundSize: '100% 60px'
            }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <span className="text-blue-400 font-bold text-sm tracking-wider uppercase mb-4 block">Why Choose Us</span>
            <h2 className="text-5xl font-bold mb-6">FullBetの支援体制</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              私たちは単なる営業代行会社ではなく、貴社の営業課題に伴走するビジネスパートナーです
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {[
              {
                number: '01',
                title: '現場理解に基づく提案',
                description: '営業現場の課題を丁寧に把握し、状況に応じた実行可能な施策をご提案します。'
              },
              {
                number: '02',
                title: 'データドリブンなアプローチ',
                description: '感覚ではなく、データに基づいた戦略立案と実行を行います。KPIの設定から効果測定まで、数字で進捗を可視化します。'
              },
              {
                number: '03',
                title: '柔軟なカスタマイズ',
                description: '企業ごとに異なる課題やニーズに合わせて、最適なサービスをカスタマイズ。画一的ではない支援設計を行います。'
              },
              {
                number: '04',
                title: '継続的なサポート体制',
                description: '定期的なフォローアップと改善提案を行い、継続的な営業力強化をサポートします。'
              }
            ].map((reason, index) => (
              <div key={index} className="flex gap-6 group">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-3xl font-bold group-hover:scale-110 transition-transform duration-300">
                    {reason.number}
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">{reason.title}</h3>
                  <p className="text-slate-300 leading-relaxed">{reason.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-blue-600 font-bold text-sm tracking-wider uppercase mb-4 block">Process</span>
            <h2 className="text-5xl font-bold text-slate-900 mb-6">ご利用の流れ</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              シンプルで分かりやすい4ステップで、営業支援サービスを開始できます
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: 'STEP 01',
                title: 'お問い合わせ',
                description: 'まずはお気軽にお問い合わせください。営業に関するお悩みをお聞かせください。',
                icon: 'ri-mail-send-line'
              },
              {
                step: 'STEP 02',
                title: '無料相談・ヒアリング',
                description: '現状の課題や目標をヒアリング。最適なソリューションをご提案します。',
                icon: 'ri-discuss-line'
              },
              {
                step: 'STEP 03',
                title: 'プラン策定',
                description: '貴社専用の営業支援プランを策定。具体的な施策とスケジュールをご提示します。',
                icon: 'ri-file-list-3-line'
              },
              {
                step: 'STEP 04',
                title: 'サービス開始',
                description: 'プランに基づいてサービスを開始。定期的な報告と改善提案を行います。',
                icon: 'ri-rocket-line'
              }
            ].map((process, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100 h-full">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center mb-6">
                    <i className={`${process.icon} text-3xl text-white`}></i>
                  </div>
                  <div className="text-blue-600 font-bold text-sm mb-3">{process.step}</div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{process.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{process.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-3xl text-blue-300"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 2px,
                rgba(255,255,255,0.1) 2px,
                rgba(255,255,255,0.1) 4px
              )`,
              backgroundSize: '60px 60px'
            }}
          ></div>
        </div>

        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span className="text-blue-400 font-bold text-sm tracking-wider uppercase mb-4 block">Contact Us</span>
            <h2 className="text-5xl font-bold mb-6">お問い合わせ</h2>
            <p className="text-xl text-slate-300">
              営業に関するお悩みやご相談は、お気軽にお問い合わせください
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-10 border border-white/20">
            <form onSubmit={handleOpenConfirm} id="contact-form">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-2">お名前 <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all text-sm"
                    placeholder="山田 太郎"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">会社名 <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all text-sm"
                    placeholder="株式会社〇〇"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-2">メールアドレス <span className="text-red-400">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all text-sm"
                    placeholder="example@company.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">電話番号</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">ご希望のサービス <span className="text-red-400">*</span></label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 pr-8 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all text-sm cursor-pointer appearance-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  <option value="" className="bg-slate-800">選択してください</option>
                  <option value="営業代行業務" className="bg-slate-800">営業代行業務</option>
                  <option value="営業コンサルティング" className="bg-slate-800">営業コンサルティング</option>
                  <option value="営業プロモーション・マーケティング支援" className="bg-slate-800">営業プロモーション・マーケティング支援</option>
                  <option value="営業研修・人材育成" className="bg-slate-800">営業研修・人材育成</option>
                  <option value="営業人材派遣" className="bg-slate-800">営業人材派遣</option>
                  <option value="その他・相談" className="bg-slate-800">その他・相談</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">お問い合わせ内容 <span className="text-red-400">*</span></label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all resize-none text-sm"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                ></textarea>
                <div className="text-right text-sm text-white/50 mt-1">{formData.message.length}/500</div>
              </div>

              <div className="mb-8">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="privacyAccepted"
                    checked={formData.privacyAccepted}
                    onChange={handleChange}
                    required
                    className="mt-1 w-5 h-5 rounded border-white/20 bg-white/10 text-blue-500 focus:ring-2 focus:ring-blue-400 cursor-pointer"
                  />
                  <span className="text-sm text-white/90 leading-relaxed group-hover:text-white transition-colors">
                    <a href="/privacy" className="text-blue-400 hover:text-blue-300 underline">プライバシーポリシー</a>に同意します <span className="text-red-400">*</span>
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={submitStatus === 'submitting'}
                className="w-full py-4 bg-gradient-to-r from-blue-500 to-cyan-400 text-white text-lg font-bold rounded-xl hover:shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                入力内容を確認する
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-12 shadow-lg border border-slate-100">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">会社情報</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
                    <i className="ri-building-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">会社名</div>
                  <div className="text-lg font-bold text-slate-900">合同会社FullBet</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
                    <i className="ri-calendar-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">設立</div>
                  <div className="text-lg font-bold text-slate-900">令和6年10月16日</div>
                </div>
              </div>
              <div className="flex gap-4 md:col-span-2">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
                    <i className="ri-map-pin-line text-xl text-white"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">所在地</div>
                  <div className="text-lg font-bold text-slate-900">
                    〒700-0945<br />
                    岡山市南区新保441番地17<br />
                    PROMENA de SOU 101
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <div className="text-3xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif' }}>FullBet</div>
            <p className="text-slate-400 mb-8">営業のプロフェッショナルが、あなたのビジネスを加速する</p>
            <div className="flex justify-center gap-6 mb-8">
              <a href="#services" className="text-slate-400 hover:text-white transition-colors cursor-pointer">サービス</a>
              <a href="#contact" className="text-slate-400 hover:text-white transition-colors cursor-pointer">お問い合わせ</a>
              <a href="/privacy" className="text-slate-400 hover:text-white transition-colors cursor-pointer">プライバシーポリシー</a>
            </div>
            <div className="border-t border-slate-800 pt-8">
              <p className="text-slate-500 text-sm">&copy; 2024 合同会社FullBet. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>

      <Modal
        isOpen={submitStatus === 'confirm'}
        title="この内容で送信しますか？"
        onClose={() => setSubmitStatus('idle')}
        actions={
          <>
            <button
              type="button"
              onClick={() => setSubmitStatus('idle')}
              className="rounded-xl border border-slate-300 px-6 py-3 font-bold text-slate-700 hover:bg-slate-100 transition-colors"
            >
              戻る
            </button>
            <button
              type="button"
              onClick={handleConfirmSubmit}
              className="rounded-xl bg-gradient-to-r from-blue-500 to-cyan-400 px-6 py-3 font-bold text-white hover:shadow-lg transition-all"
            >
              送信する
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {confirmationItems.map((item) => (
            <div key={item.label} className="rounded-2xl bg-slate-50 px-4 py-3">
              <div className="text-sm font-bold text-slate-500">{item.label}</div>
              <div className="mt-1 whitespace-pre-wrap font-semibold text-slate-900">{item.value}</div>
            </div>
          ))}
          <div className="rounded-2xl bg-slate-50 px-4 py-3">
            <div className="text-sm font-bold text-slate-500">プライバシーポリシー同意</div>
            <div className="mt-1 font-semibold text-slate-900">同意する</div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={submitStatus === 'submitting'} title="送信中です" closeOnBackdrop={false}>
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-500"></div>
          <p className="font-semibold text-slate-700">お問い合わせ内容を送信しています。しばらくお待ちください。</p>
        </div>
      </Modal>

      <Modal
        isOpen={submitStatus === 'success'}
        title="送信完了"
        onClose={() => setSubmitStatus('idle')}
        actions={
          <button
            type="button"
            onClick={() => setSubmitStatus('idle')}
            className="rounded-xl bg-gradient-to-r from-blue-500 to-cyan-400 px-6 py-3 font-bold text-white hover:shadow-lg transition-all"
          >
            閉じる
          </button>
        }
      >
        <p className="font-semibold">{dialogMessage}</p>
      </Modal>

      <Modal
        isOpen={submitStatus === 'error'}
        title="送信できませんでした"
        onClose={() => setSubmitStatus('idle')}
        actions={
          <button
            type="button"
            onClick={() => setSubmitStatus('idle')}
            className="rounded-xl bg-gradient-to-r from-blue-500 to-cyan-400 px-6 py-3 font-bold text-white hover:shadow-lg transition-all"
          >
            閉じる
          </button>
        }
      >
        <p className="font-semibold">{dialogMessage}</p>
      </Modal>
    </div>
  );
}
